import { Icon } from '@design-system/icon';
import { LinkProps } from '@design-system/link';
import classNames from 'classnames';
import { Link } from 'gatsby';
import React, { ReactElement, useContext } from 'react';

import { ExternalLinkDialogContext } from '~/context/ExternalLinkDialogContext';
import { useRedirects } from '~/context/redirects';
import { deleteTrailingSlash } from '~/utils/stringUtils';

const TMPLinkWrap = ({
  variant = 'primary',
  size = 'small',
  children,
  className,
  iconPrefix,
  iconSuffix,
  href,
  onClick,
  target,
  ...htmlProperties
}: LinkProps): ReactElement => {
  const { redirects } = useRedirects();

  const url =
    redirects?.find(
      (item) =>
        deleteTrailingSlash(item.fromPath, true) ===
        deleteTrailingSlash(href, true)
    )?.toPath || href;

  const IconPrefixComponent = iconPrefix?.component || Icon;
  const IconSuffixComponent = iconSuffix?.component || Icon;

  const { displayModal } = useContext(ExternalLinkDialogContext);

  const isInternal = /(^\/(?!\/))/.test(url) && target !== '_blank';
  const isAnchorLink = /(^#)/.test(url);

  const commonProps = {
    className: classNames(
      'cds-link',
      className,
      `cds-link--${variant}`,
      `cds-link--${variant}-${size}`
    ),
    ...htmlProperties,
  };

  const content = (
    <>
      {iconPrefix?.props && (
        <IconPrefixComponent
          className={classNames('icon__prefix', `icon--${variant}-icon`)}
          {...iconPrefix.props}
        />
      )}
      <span
        className={classNames('cds-link__inner', {
          'cds-sr-only': variant.includes('-icon'),
        })}
      >
        {children}
      </span>
      {iconSuffix?.props && (
        <IconSuffixComponent
          className={classNames('icon__suffix', `icon--${variant}-icon`)}
          {...iconSuffix.props}
        />
      )}
    </>
  );

  if (isInternal) {
    return (
      <Link to={url} {...(commonProps as any)} onClick={onClick}>
        {content}
      </Link>
    );
  }

  if (isAnchorLink) {
    return (
      <a href={url} {...commonProps}>
        {content}
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => displayModal(e, url, onClick)}
      {...commonProps}
    >
      {content}
    </a>
  );
};

export default TMPLinkWrap;
